import type { PresetId } from '@manudev/pipeline/presets';
import * as THREE from 'three';
import { net } from '@/lib/net';

export type { PresetId };

export const TEXTURE_BASE = '/textures/';

export interface PresetMapPaths {
  albedo: string;
  normal: string;
  roughness: string;
}

export interface MaterialPreset {
  id: PresetId;
  // relative to TEXTURE_BASE; a photo set replaces the files, not this table
  maps: PresetMapPaths | null;
  // metres one texture tile covers
  tiling: number;
  metalness: number;
  // multiplies the roughness map (or is the roughness without one)
  roughness: number;
  normalScale: number;
  tint: string;
  // 0 shows the texture's own colour, 1 multiplies it with the IFC colour instead of the tint
  keepColor: number;
  // what translucent parts of this preset show once the materials are on
  opacity?: number;
}

const set = (id: string): PresetMapPaths => ({
  albedo: `${id}/albedo.png`,
  normal: `${id}/normal.png`,
  roughness: `${id}/roughness.png`,
});

const preset = (p: Omit<MaterialPreset, 'maps'> & { maps?: PresetMapPaths | null }) =>
  ({ maps: set(p.id), ...p }) as MaterialPreset;

export const PRESETS: Record<PresetId, MaterialPreset> = {
  concrete: preset({
    id: 'concrete',
    tiling: 2,
    metalness: 0,
    roughness: 1,
    normalScale: 0.6,
    tint: '#ffffff',
    keepColor: 0,
  }),
  brick: preset({
    id: 'brick',
    tiling: 1,
    metalness: 0,
    roughness: 1,
    normalScale: 1,
    tint: '#ffffff',
    keepColor: 0,
  }),
  plaster: preset({
    id: 'plaster',
    tiling: 2,
    metalness: 0,
    roughness: 1,
    normalScale: 0.5,
    tint: '#ffffff',
    keepColor: 0.3,
  }),
  wood: preset({
    id: 'wood',
    tiling: 1,
    metalness: 0,
    roughness: 1,
    normalScale: 0.6,
    tint: '#ffffff',
    keepColor: 0.5,
  }),
  glass: preset({
    id: 'glass',
    maps: null,
    tiling: 1,
    metalness: 0,
    roughness: 0.05,
    normalScale: 0,
    tint: '#b8d4e0',
    keepColor: 0.5,
    opacity: 0.35,
  }),
  metal: preset({
    id: 'metal',
    tiling: 1,
    metalness: 0.85,
    roughness: 1,
    normalScale: 0.3,
    tint: '#ffffff',
    keepColor: 0.3,
  }),
  roof: preset({
    id: 'roof',
    tiling: 1.2,
    metalness: 0,
    roughness: 1,
    normalScale: 1,
    tint: '#ffffff',
    keepColor: 0,
  }),
  floor: preset({
    id: 'floor',
    tiling: 0.6,
    metalness: 0,
    roughness: 1,
    normalScale: 0.8,
    tint: '#ffffff',
    keepColor: 0,
  }),
  generic: preset({
    id: 'generic',
    tiling: 2,
    metalness: 0,
    roughness: 1,
    normalScale: 0.4,
    tint: '#ffffff',
    keepColor: 1,
  }),
};

export function presetOf(id: string): MaterialPreset {
  return PRESETS[id as PresetId] ?? PRESETS.generic;
}

export interface PresetMaps {
  albedo: THREE.Texture;
  normal: THREE.Texture;
  roughness: THREE.Texture;
}

const loader = new THREE.TextureLoader();
const loaded = new Map<PresetId, PresetMaps>();

function load(path: string, colorSpace: THREE.ColorSpace): THREE.Texture {
  const texture = loader.load(TEXTURE_BASE + path);
  texture.colorSpace = colorSpace;
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 8;
  return texture;
}

// One texture set per preset for the whole page, loaded the first time a batch asks for it.
// Albedo is sRGB; normal and roughness are data.
export function presetMaps(p: MaterialPreset): PresetMaps | null {
  if (!p.maps) return null;
  let maps = loaded.get(p.id);
  if (!maps) {
    maps = {
      albedo: load(p.maps.albedo, THREE.SRGBColorSpace),
      normal: load(p.maps.normal, THREE.NoColorSpace),
      roughness: load(p.maps.roughness, THREE.NoColorSpace),
    };
    loaded.set(p.id, maps);
  }
  return maps;
}

// Total size of the texture sets as the pipeline wrote them.
export async function textureBytes(): Promise<number | null> {
  try {
    const res = await net(TEXTURE_BASE + 'manifest.json');
    if (!res.ok) return null;
    return ((await res.json()) as { bytes?: number }).bytes ?? null;
  } catch {
    return null;
  }
}
